import { Dispatch } from 'redux';
import { Duration, GuestsObject } from "@/data/types";

// Action Types
const SAVE_DURATION = 'SAVE_DURATION';
const SAVE_MEETING_POINT = 'SAVE_MEETING_POINT';
const SAVE_EMAIL = 'SAVE_EMAIL';
const SAVE_TRAVELER_NAME = 'SAVE_TRAVELER_NAME';
const SAVE_SPECIAL_REQUIREMENTS = 'SAVE_SPECIAL_REQUIREMENTS';
const SAVE_GUIDE_LANGUAGE = 'SAVE_GUIDE_LANGUAGE';
const SAVE_TRAVELLERS = 'SAVE_TRAVELLERS';
const SAVE_PHONE_NUMBER = 'SAVE_PHONE_NUMBER';
const SAVE_DATE = 'SAVE_DATE';
const SAVE_TIME = 'SAVE_TIME';
const RESET_BOOKING = 'RESET_BOOKING';
const SAVE_TOUR = 'SAVE_TOUR';
const SAVE_PRICE = 'SAVE_PRICE';
const SAVE_TRAVELER_TYPE = 'SAVE_TRAVELER_TYPE';
const SAVE_PASSENGER_RELATION = 'SAVE_PASSENGER_RELATION';
const RE_INIT_BOOKING = 'RE_INIT_BOOKING';

// Action Interfaces

interface SavePassengerRelationAction {
  type: typeof SAVE_PASSENGER_RELATION;
  payload: string;
}

interface SaveDurationAction {
  type: typeof SAVE_DURATION;
  payload: Duration | null;
}

interface SaveMeetingPointAction {
  type: typeof SAVE_MEETING_POINT;
  payload: string;
}

interface SaveEmailAction {
  type: typeof SAVE_EMAIL;
  payload: string;
}

interface SaveTravelerTypeAction {
  type: typeof SAVE_TRAVELER_TYPE;
  payload: string;
}

interface SaveTravelerNameAction {
  type: typeof SAVE_TRAVELER_NAME;
  payload: string;
}

interface SaveSpecialRequirementsAction {
  type: typeof SAVE_SPECIAL_REQUIREMENTS;
  payload: string;
}

interface SaveGuideLanguageAction {
  type: typeof SAVE_GUIDE_LANGUAGE;
  payload: string; // Only the language code
}

interface SaveTravellersAction {
  type: typeof SAVE_TRAVELLERS;
  payload: GuestsObject;
}

interface SavePhoneNumberAction {
  type: typeof SAVE_PHONE_NUMBER;
  payload: string;
}

interface SaveDateAction {
  type: typeof SAVE_DATE;
  payload: { date: string | null };
}

interface SaveTimeAction {
  type: typeof SAVE_TIME;
  payload: { time: string | null };
}

interface SaveTourAction {
  type: typeof SAVE_TOUR;
  payload: any;
}

interface SavePriceAction {
  type: typeof SAVE_PRICE;
  payload: number;
}

interface ResetBookingAction {
  type: typeof RESET_BOOKING;
}

interface ReInitBookingAction {
  type: typeof RE_INIT_BOOKING;
  payload: any;
}

type BookingActions =
  | SaveDurationAction
  | SaveMeetingPointAction
  | SaveEmailAction
  | SaveTravelerTypeAction
  | SaveTravelerNameAction
  | SaveSpecialRequirementsAction
  | SaveGuideLanguageAction
  | SaveTravellersAction
  | SavePhoneNumberAction
  | SaveDateAction
  | SaveTimeAction
  | SaveTourAction
  | SavePriceAction
  | ResetBookingAction
  | ReInitBookingAction
  | SavePassengerRelationAction;

  
export const savePassengerRelation = (relation: string): SavePassengerRelationAction => ({
  type: SAVE_PASSENGER_RELATION,
  payload: relation,
});

// Action Creators
export const saveDuration = (duration: Duration | null): SaveDurationAction => ({
  type: SAVE_DURATION,
  payload: duration,
});

export const saveMeetingPoint = (meetingPoint: string): SaveMeetingPointAction => ({
  type: SAVE_MEETING_POINT,
  payload: meetingPoint,
});

export const saveEmail = (email: string): SaveEmailAction => ({
  type: SAVE_EMAIL,
  payload: email,
});

export const saveTravelerType = (travelerType: string): SaveTravelerTypeAction => ({
  type: SAVE_TRAVELER_TYPE,
  payload: travelerType,
});

export const saveTravelerName = (name: string): SaveTravelerNameAction => ({
  type: SAVE_TRAVELER_NAME,
  payload: name,
});

export const saveSpecialRequirements = (specialRequirements: string): SaveSpecialRequirementsAction => ({
  type: SAVE_SPECIAL_REQUIREMENTS,
  payload: specialRequirements,
});

export const saveGuideLanguage = (languageCode: string): SaveGuideLanguageAction => ({
  type: SAVE_GUIDE_LANGUAGE,
  payload: languageCode,
});

export const saveTravellers = (travellers: GuestsObject): SaveTravellersAction => ({
  type: SAVE_TRAVELLERS,
  payload: travellers,
});

export const savePhoneNumber = (phoneNumber: string): SavePhoneNumberAction => ({
  type: SAVE_PHONE_NUMBER,
  payload: phoneNumber,
});

export const saveDate = (date: { date: string | null }): SaveDateAction => ({
  type: SAVE_DATE,
  payload: date,
});

export const saveTime = (time: { time: string | null }): SaveTimeAction => ({
  type: SAVE_TIME,
  payload: time,
});

export const saveTour = (tour: any) => async (dispatch: Dispatch) => {
  dispatch({
    type: SAVE_TOUR,
    payload: tour,
  });
};

// Save the line-total (EUR) the user is about to pay. Used by the
// guest auth step (`/checkout/login`) to surface a real amount in the
// order recap before the cart has been created.
export const savePrice = (price: number) => ({
  type: SAVE_PRICE,
  payload: price,
});

export const resetBooking = (): ResetBookingAction => ({
  type: RESET_BOOKING,
});

export const reInit = (item: any) => async (dispatch: Dispatch) => {
  dispatch({
    type: RE_INIT_BOOKING,
    payload: item,
  });
};

// State Type
interface BookingState {
  selectedDuration: Duration | null;
  meetingPoint: string | null;
  email: string | null;
  travelerType: string | null;
  name: string | null;
  specialRequirements: string | null;
  guideLanguage: string | null; // Store only the selected language code
  travellers: GuestsObject;
  phoneNumber: string | null;
  datetime: { date: string | null; time: string | null };
  tour: any | null;
  price: number;
  priceOriginalCurrency: number;
  passengerRelation: string | null;
}

// Initial State
const initialState: BookingState = {
  selectedDuration: null,
  meetingPoint: null,
  email: null,
  travelerType: null,
  name: null,
  specialRequirements: null,
  guideLanguage: null,
  travellers: { adults: 0, children: 0, infants: 0 },
  phoneNumber: null,
  datetime: { date: null, time: null },
  tour: null,
  price: 0,
  priceOriginalCurrency: 0,
  passengerRelation: null
};

// Reducer
const bookingReducer = (
  state: BookingState = initialState,
  action: BookingActions
): BookingState => {
  switch (action.type) {
    case SAVE_DURATION: {
      return {
        ...state,
        selectedDuration: action.payload,
      };
    }
    case SAVE_MEETING_POINT: {
      return {
        ...state,
        meetingPoint: action.payload,
      };
    }
    case SAVE_EMAIL: {
      return {
        ...state,
        email: action.payload,
      };
    }
    case SAVE_TRAVELER_TYPE: {
      return {
        ...state,
        travelerType: action.payload,
      };
    }
    case SAVE_TRAVELER_NAME: {
      return {
        ...state,
        name: action.payload,
      };
    }
    case SAVE_SPECIAL_REQUIREMENTS: {
      return {
        ...state,
        specialRequirements: action.payload,
      };
    }
    case SAVE_GUIDE_LANGUAGE: {
      return {
        ...state,
        guideLanguage: action.payload, // Store only the selected language code
      };
    }
    case SAVE_TRAVELLERS: {
      return {
        ...state,
        travellers: action.payload,
      };
    }
    case SAVE_PHONE_NUMBER: {
      return {
        ...state,
        phoneNumber: action.payload,
      };
    }
    case SAVE_DATE: {
      return {
        ...state,
        datetime: { ...state.datetime, date: action.payload.date }
      };
    }
    case SAVE_TIME: {
      return {
        ...state,
        datetime: { ...state.datetime, time: action.payload.time }
      };
    }
    case SAVE_TOUR: {
      return {
        ...state,
        tour: action.payload,
      };
    }
    case SAVE_PRICE: {
      return {
        ...state,
        price: Number(action.payload) || 0,
      };
    }
    case SAVE_PASSENGER_RELATION:{
      return { 
        ...state, passengerRelation: action.payload 
      };
    }
    case RE_INIT_BOOKING: {
      return {
        ...initialState,
        ...(action.payload),
      };
    }
    case RESET_BOOKING: {
      return initialState;
    }
    default: {
      return state;
    }
  }
};

export default bookingReducer;
